import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { OrderListFilter } from '../model/list-params';
import type { OffersMode, OffersView } from '../lib/offers';
import { legacyOffersView } from '../lib/offers-legacy';
import { newOffersView } from '../lib/offers-new';
import { SEARCH_ALARM_LIMIT, SEARCH_ALARM_POLL_MS } from '../lib/search-alarm';
import { ordersApi, type OrderActorRole, type OrderOptionsFilter } from './orders-api';

/**
 * Query-key factory + query-options for the real Order domain. Mutations
 * invalidate through `ordersKeys` (never an inline array). Polling lives in the
 * options (location/battery) rather than a manual interval. The cpanel cache
 * had to keep keys byte-identical; v2 has no persisted cache, so keys are
 * structured cleanly — only internal consistency matters.
 */
export const ordersKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersKeys.all, 'list'] as const,
  list: (filter: OrderListFilter) => [...ordersKeys.lists(), filter] as const,
  stageTabCounts: (filter: object) =>
    [...ordersKeys.all, 'stage-tab-counts', filter] as const,
  vendorOptions: (filter: Omit<OrderOptionsFilter, 'skip'>) =>
    [...ordersKeys.all, 'vendor-options', filter] as const,
  courierOptions: (filter: Omit<OrderOptionsFilter, 'skip'>) =>
    [...ordersKeys.all, 'courier-options', filter] as const,
  actor: (role: OrderActorRole, id: string | number) =>
    [...ordersKeys.all, 'actor', role, String(id)] as const,
  searchAlarm: (fromDate: string, tillDate: string) =>
    [...ordersKeys.all, 'search-alarm', fromDate, tillDate] as const,
  details: () => [...ordersKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...ordersKeys.details(), String(id)] as const,
  assignment: (id: string | number) =>
    [...ordersKeys.all, 'assignment', String(id)] as const,
  offers: (id: string | number, mode: OffersMode) =>
    [...ordersKeys.all, 'offers', mode, String(id)] as const,
  logs: (id: string | number) => [...ordersKeys.all, 'logs', String(id)] as const,
  courierLocation: (orderId: string | number, courierId: string | number) =>
    [...ordersKeys.all, 'courier-location', String(orderId), String(courierId)] as const,
  batteryLocation: (orderId: string | number, courierId: string | number) =>
    [...ordersKeys.all, 'battery-location', String(orderId), String(courierId)] as const,
};

/** Poll interval for live courier/battery location (cpanel: 5 s). */
export const LIVE_LOCATION_POLL_MS = 5_000;

/** Next `skip` for an options page, or undefined once every row is loaded. */
function nextOptionsSkip(
  lastPage: { data: unknown[]; count: number },
  pages: { data: unknown[] }[],
): number | undefined {
  const loaded = pages.reduce((total, page) => total + page.data.length, 0);
  if (lastPage.data.length === 0 || loaded >= lastPage.count) return undefined;
  return loaded;
}

export const ordersQueries = {
  list: (filter: OrderListFilter) =>
    queryOptions({
      queryKey: ordersKeys.list(filter),
      queryFn: () => ordersApi.list(filter),
    }),

  stageTabCounts: (filter: object, enabled = true) =>
    queryOptions({
      queryKey: ordersKeys.stageTabCounts(filter),
      queryFn: () => ordersApi.stageTabCounts(filter),
      enabled,
      refetchInterval: enabled ? 30_000 : false,
    }),

  vendorOptions: (filter: Omit<OrderOptionsFilter, 'skip'>, enabled: boolean) =>
    infiniteQueryOptions({
      queryKey: ordersKeys.vendorOptions(filter),
      queryFn: ({ pageParam }) => ordersApi.vendorOptions({ ...filter, skip: pageParam }),
      initialPageParam: 0,
      getNextPageParam: nextOptionsSkip,
      enabled,
    }),

  courierOptions: (filter: Omit<OrderOptionsFilter, 'skip'>, enabled: boolean) =>
    infiniteQueryOptions({
      queryKey: ordersKeys.courierOptions(filter),
      queryFn: ({ pageParam }) =>
        ordersApi.courierOptions({ ...filter, skip: pageParam }),
      initialPageParam: 0,
      getNextPageParam: nextOptionsSkip,
      enabled,
    }),

  /**
   * The night alarm's in-search poll (cpanel `useSearchAlarmMonitor`): every
   * in-search order inside a rolling window, refetched every 10 s even while
   * the tab is in the background.
   */
  searchAlarm: (window: { from_date: string; till_date: string }, enabled: boolean) =>
    queryOptions({
      queryKey: ordersKeys.searchAlarm(window.from_date, window.till_date),
      queryFn: () =>
        ordersApi.list({
          limit: SEARCH_ALARM_LIMIT,
          skip: 0,
          is_in_search: true,
          ...window,
        }),
      enabled,
      placeholderData: (prev) => prev,
      refetchInterval: enabled ? SEARCH_ALARM_POLL_MS : false,
      refetchIntervalInBackground: true,
    }),

  actor: (role: OrderActorRole, id: string | number, enabled: boolean) =>
    queryOptions({
      queryKey: ordersKeys.actor(role, id),
      queryFn: () => ordersApi.actor(role, id),
      enabled,
      staleTime: Infinity,
    }),

  detail: (id: string | number) =>
    queryOptions({
      queryKey: ordersKeys.detail(id),
      queryFn: () => ordersApi.getById(id),
    }),

  assignment: (id: string | number) =>
    queryOptions({
      queryKey: ordersKeys.assignment(id),
      queryFn: () => ordersApi.assignment(id),
    }),

  /**
   * Offers for the visualization in one mode, already normalised to the shared
   * {@link OffersView}. Always re-read on open (cpanel: `staleTime: 0` +
   * `refetchOnMount: 'always'`); the socket `refresh` push invalidates
   * `ordersKeys.all`, which keeps an open map live. Each mode has its own cache
   * entry, so switching back is instant and then refreshes.
   */
  offers: (id: string | number, mode: OffersMode, enabled = true) =>
    queryOptions({
      queryKey: ordersKeys.offers(id, mode),
      queryFn: async (): Promise<OffersView> =>
        mode === 'legacy'
          ? legacyOffersView(await ordersApi.legacyOffers(id))
          : newOffersView(await ordersApi.offers(id)),
      enabled,
      staleTime: 0,
      refetchOnMount: 'always',
    }),

  logs: (id: string | number) =>
    queryOptions({
      queryKey: ordersKeys.logs(id),
      queryFn: () => ordersApi.logs(id),
    }),

  courierLocation: (
    orderId: string | number,
    courierId: string | number,
    enabled: boolean,
  ) =>
    queryOptions({
      queryKey: ordersKeys.courierLocation(orderId, courierId),
      queryFn: () => ordersApi.courierLocation(orderId, courierId),
      enabled,
      refetchInterval: enabled ? LIVE_LOCATION_POLL_MS : false,
    }),

  batteryLocation: (
    orderId: string | number,
    courierId: string | number,
    enabled: boolean,
  ) =>
    queryOptions({
      queryKey: ordersKeys.batteryLocation(orderId, courierId),
      queryFn: () => ordersApi.courierBatteryLocation(orderId, courierId),
      enabled,
      refetchInterval: enabled ? LIVE_LOCATION_POLL_MS : false,
    }),
};
